import { reportBrowserRuntimeError } from "@diverge/observability/browser";
import { dashboardObservability } from "./observability";

/**
 * Every deploy emits freshly hashed asset files and deletes the previous ones.
 * A tab that was opened before the deploy still holds the old `index.html`
 * graph, so the next dynamic import it makes asks for a chunk the server no
 * longer has and the browser throws "Failed to fetch dynamically imported
 * module: .../assets/FlowDiagram-SnZzVDRm.js". The only real cure is a full
 * document reload, which fetches the new `index.html` and its new hashes.
 */

const RELOAD_MARKER = "di:stale-chunk-reload-at";

// A reload that doesn't fix things must not loop: if the same tab hits another
// chunk failure this soon after reloading, show the error instead.
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|dynamically imported module|chunkloaderror|loading chunk \S+ failed|unable to preload css/i;

export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERN.test(message);
}

function readMarker(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARKER)) || 0;
  } catch {
    // Private mode / blocked storage: treat as "never reloaded".
    return 0;
  }
}

function writeMarker(at: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(at));
  } catch {
    // Without storage we can still reload once; the browser's own error
    // surface takes over if that loops.
  }
}

// A reload is not instant: the document keeps running until the browser swaps
// it out, and several chunks can fail in that window. They should all wait for
// the reload rather than each render an error.
let reloadInFlight = false;

/**
 * Reload the page so the tab picks up the current build. Returns true when a
 * reload is happening (the caller should keep waiting), false when one was
 * already tried a moment ago and did not help — then the caller should surface
 * an error instead of spinning.
 */
export function reloadForStaleChunk(): boolean {
  if (typeof window === "undefined") return false;
  if (reloadInFlight) return true;

  const now = Date.now();
  if (now - readMarker() < RELOAD_COOLDOWN_MS) return false;

  writeMarker(now);
  reloadInFlight = true;
  window.location.reload();
  return true;
}

/**
 * Vite's `vite:preloadError`: a code-split chunk could not be loaded. A tab
 * whose build a deploy replaced reloads onto the current one, which recovers
 * it, so that only leaves a breadcrumb. A failure that a reload moments ago did
 * not fix is the tab's resource_load failure.
 */
export function handleChunkPreloadError(): void {
  if (reloadForStaleChunk())
    dashboardObservability.addBreadcrumb("browser.resource.failed", {
      reason: "stale_chunk_reload",
    });
  else reportBrowserRuntimeError(dashboardObservability, undefined, "resource_load");
}
